import { useTranslation } from "react-i18next";
import PageTitle from "../../../layouts/PageTitle";
import { Alert, Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useState, useEffect } from "react";
import axiosInstance from "../../../../services/AxiosInstance";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import LoadingCenter from "../../../components/Common/LoadingCenter";
import ImageDownload from "../../../components/Custom/Common/ImageDownload";
import MonthSelect from "../../../components/Common/MonthSelect";
import YearSelect from "../../../components/Common/YearSelect";
import { endYear, getDateRange, startYear } from "../../../../utils/DateUtils";
import CalculatorIcon from "../../../components/Icon/CalculatorIcon";
import CalculateCommission from "./commission/CalculateCommission";
import { warnAlert } from "../../../components/alert/defaultAlert";
import PotPieChart from "../../../components/Custom/Gamification/Chart/PotPieChart";
import PotBarChart from "../../../components/Custom/Gamification/Chart/PotBarChart";
import DistributionPot from "../../../components/Custom/Gamification/DistributionPot";
import PotPercentageNotice, { isPotPercentageMissing } from "../../../components/Custom/Gamification/PotPercentageNotice";
import { useBranch } from "../../../../context/BranchContext";
import "./PotDashboard.css";

const EmployeePotDashboard = () => {

    const { t } = useTranslation();
    // Filial do cabeçalho: com uma selecionada, o pote é a parte dela nas mensalidades (desde
    // outubro de 2026, pelos atendimentos de assinatura) dividida entre a equipe dela.
    const { selectedBranch } = useBranch();
    const branchId = selectedBranch?.id ?? selectedBranch?.value ?? null;
    const [showLoading, setShowLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [reportParams, setReportParams] = useState(null);
    // Busca do bloco de colaboradores. Com a equipe inteira em cards, achar uma pessoa
    // específica virava rolagem; o filtro é só de exibição e não toca no rateio.
    const [employeeSearch, setEmployeeSearch] = useState('');

    // Pré-preenche mês e ano com a data corrente ao abrir a tela.
    // Usa useEffect para garantir que t() já carregou as traduções dos meses.
    const MONTH_KEYS = ['january','february','march','april','may','june',
                        'july','august','september','october','november','december'];
    useEffect(() => {
        const now = new Date();
        const m = now.getMonth() + 1;
        const y = now.getFullYear();
        setMonth({ value: m, label: t('month.' + MONTH_KEYS[m - 1]) });
        setYear({ value: y, label: y });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Ignora acento e caixa: quem digita "jose" precisa achar "José".
    const normalize = (value) => String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

    const searchTerm = normalize(employeeSearch);
    const visibleItems = searchTerm
        ? items.filter(item => normalize(item.fullName).includes(searchTerm))
        : items;

    // Trocar a filial no cabeçalho refaz o cálculo já exibido. Antes a tela ignorava a filial e
    // mostrava sempre o pote da empresa toda, mesmo com uma unidade selecionada.
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (reportParams) {
            onCalculate();
        }
    }, [branchId]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const onCalculate = () => {
        // Validações
        if (!month) {
            warnAlert(t('validation.required.month'));
            return;
        }
        if (!year) {
            warnAlert(t('validation.required.year'));
            return;
        }

        // O endpoint consolida o rateio, os cards e os dados dos gráficos. Assim todos os
        // blocos usam o mesmo cálculo do período, sem uma chamada por colaborador.
        setShowLoading(true);
        const dateRange = getDateRange(month.value, year.value);
        axiosInstance.get(`${Api.gamification.pot}/dashboard/interval/${dateRange.start}/${dateRange.end}`, {
            params: { branchId }
        })
            .then((resp) => {
                const employees = [...(resp.data.employees || [])]
                    .sort((a, b) => a.fullName.localeCompare(b.fullName));
                setDashboard(resp.data);
                setItems(employees);
                // A filial que valeu no cálculo vem da resposta: antes de outubro de 2026 o
                // pote é sempre da empresa inteira, mesmo com uma filial no cabeçalho.
                setReportParams({
                    month: month.value,
                    year: year.value,
                    branchId: resp.data.branchId ?? null,
                    branchIgnored: Boolean(resp.data.branchIgnored)
                });
            })
            .catch(error => ApiError(error))
            .finally(() => setShowLoading(false));
    }

    return (
        <>
            <PageTitle activeMenu={t('gamification.pot')} motherMenu={t('gamification.label')} />
            <div className="pot-dashboard">
                <div className="form-layout__panel pot-dashboard__filters">
                    <div className="form-layout__panel-header">
                        <div>
                            <h5 className="mb-1">{t('filters')}</h5>
                            <p className="mb-0">{t('gamification.pot_filters_hint')}</p>
                        </div>
                    </div>

                    <Row className="g-3 align-items-end">
                        <Col md={3}>
                            <MonthSelect
                                placeholder={t('month.label')}
                                ordinal={false}
                                value={month}
                                onChange={(value) => setMonth(value)}
                            />
                        </Col>
                        <Col md={3}>
                            <YearSelect
                                placeholder={t('year')}
                                value={year}
                                startYear={startYear()}
                                endYear={endYear()}
                                onChange={(value) => setYear(value)}
                            />
                        </Col>
                        <Col md={3}>
                            <Button className="btn btn-primary pot-dashboard__calculate-btn" onClick={() => onCalculate()}>
                                <CalculatorIcon /> {t('calculate')}
                            </Button>
                        </Col>
                    </Row>
                </div>

                {!reportParams && !showLoading && (
                    <Alert variant="light" className="pot-dashboard__empty-state">
                        {t('gamification.pot_dashboard_empty_hint')}
                    </Alert>
                )}

                {showLoading ? (
                    <LoadingCenter className={"mt-5"} />
                ) : dashboard && isPotPercentageMissing(dashboard.distribution?.potCommission) ? (
                    <PotPercentageNotice />
                ) : (
                    reportParams && (
                        <>
                            {reportParams.branchIgnored && (
                                <Alert variant="info" className="pot-dashboard__branch-notice">
                                    <i className="fa-solid fa-circle-info me-2"></i>
                                    {t('gamification.pot_branch_ignored')}
                                </Alert>
                            )}

                            <Row className="g-4 mb-4">
                                <Col md={12}>
                                    <DistributionPot
                                        month={reportParams.month}
                                        year={reportParams.year}
                                        branchId={reportParams.branchId}
                                        distribution={dashboard?.distribution}
                                        onAdjustmentChange={onCalculate}
                                    />
                                </Col>
                            </Row>

                            <div className="pot-dashboard__section-head">
                                <h5>{t('charts')}</h5>
                                <p>{t('gamification.pot_charts_hint')}</p>
                            </div>

                            <Row className="g-4 mb-4">
                                <Col lg={5}>
                                    <div className="pot-dashboard__chart-card pot-dashboard__chart-card--center">
                                        <div className="pot-dashboard__chart-head">
                                            <h6>{t('gamification.pot_points_distribution_title')}</h6>
                                            <p>{t('gamification.pot_points_distribution_hint')}</p>
                                        </div>
                                        <PotPieChart
                                            data={(dashboard?.distribution?.distribution || [])
                                                .filter(item => Number(item.points || 0) > 0)
                                                .map(item => item.points)}
                                            labels={(dashboard?.distribution?.distribution || [])
                                                .filter(item => Number(item.points || 0) > 0)
                                                .map(item => item.employee)}
                                            width={320}
                                            height={320}
                                        />
                                    </div>
                                </Col>
                                <Col lg={7}>
                                    <div className="pot-dashboard__chart-card">
                                        <div className="pot-dashboard__chart-head">
                                            <h6>{t('gamification.pot_ranking_title')}</h6>
                                            <p>{t('gamification.pot_ranking_hint')}</p>
                                        </div>
                                        <PotBarChart
                                            data={(dashboard?.distribution?.distribution || [])
                                                .filter(item => Number(item.points || 0) > 0)
                                                .map(item => item.points)}
                                            labels={(dashboard?.distribution?.distribution || [])
                                                .filter(item => Number(item.points || 0) > 0)
                                                .map(item => item.employee)}
                                            className="mt-2"
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <div className="pot-dashboard__section-head">
                                <h5>{t('employees')}</h5>
                                <p>{t('gamification.pot_employee_cards_hint')}</p>
                            </div>

                            {/* Busca depois do cabeçalho inteiro, título e explicação, e antes
                                dos cards. */}
                            <InputGroup className="pot-dashboard__employee-search">
                                <InputGroup.Text>
                                    <i className="fas fa-magnifying-glass"></i>
                                </InputGroup.Text>
                                <Form.Control
                                    type="search"
                                    placeholder="Buscar colaborador pelo nome"
                                    value={employeeSearch}
                                    onChange={(event) => setEmployeeSearch(event.target.value)}
                                />
                            </InputGroup>

                            <Row className="g-4">
                                {visibleItems.length > 0 ? visibleItems.map((item) => (
                                    <Col xl={4} sm={6} key={item.id}>
                                        <div className="pot-dashboard__employee-card">
                                            <div className="pot-dashboard__employee-top">
                                                <div className="pot-dashboard__employee-avatar">
                                                    <ImageDownload
                                                        id={item.id}
                                                        path={Api.main.employee}
                                                        circle={true}
                                                        style={{ width: "84px", height: "84px", objectFit: "cover" }}
                                                        name={item.fullName}
                                                    />
                                                </div>
                                                <div>
                                                    <h6 className="mb-1">{item.fullName}</h6>
                                                    <p className="mb-0">{t('gamification.pot_employee_card_hint')}</p>
                                                </div>
                                            </div>

                                            <div className="pot-dashboard__employee-body">
                                                <CalculateCommission
                                                    entityId={item.id}
                                                    month={reportParams.month}
                                                    year={reportParams.year}
                                                    branchId={reportParams.branchId}
                                                    data={item}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                )) : (
                                    <Col md={12}>
                                        <Alert variant="light" className="pot-dashboard__empty-state">
                                            {searchTerm && items.length > 0
                                                ? 'Nenhum colaborador encontrado com esse nome.'
                                                : t('gamification.pot_no_employees_found')}
                                        </Alert>
                                    </Col>
                                )}
                            </Row>
                        </>
                    )
                )}
            </div>
        </>
    );
}

export default EmployeePotDashboard;
